import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from './hooks/useSoundEffects';
import { getImmediateNextMilestoneBonusFromLevel } from './config/exploreUnknownLevelingMilestones';
import { exploreUnknownXpBarPercent } from './utils/exploreUnknownXpBarPercent';
import './ExploreUnknownXpGainOverlay.css';

/**
 * Explore Unknown — XP gain overlay shown on the AfterBattle victory screen.
 *
 * Visual language matches the level-tree header (circular gold LV badge,
 * parchment-dark plaque, slim cyan→purple→gold XP bar, uppercase gold labels).
 *
 * Animation:
 *   1. XP count-up + bar fill (~900 ms).
 *   2. On level-up: badge flips to the new level, gold shockwave ring expands,
 *      right column crossfades to "LEVEL X → Y", bar resettles at the new
 *      level's actual fill. Total ~2200 ms before onAnimationComplete.
 *
 * @param {{ grant: object, onAnimationComplete: () => void }} props
 */
export default function ExploreUnknownXpGainOverlay({ grant, onAnimationComplete }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState('xp');
  const [displayXp, setDisplayXp] = useState(0);
  const [barFillPct, setBarFillPct] = useState(0);
  const onCompleteRef = useRef(onAnimationComplete);
  onCompleteRef.current = onAnimationComplete;

  const xpGained = Number(grant?.xpGained) || 0;
  const levelBefore = Number(grant?.levelBefore) || 1;
  const levelAfter = Number(grant?.levelAfter) || levelBefore;
  const leveledUp = grant?.leveledUp === true;
  const xpIntoLevel = Number(grant?.xpIntoLevel) || 0;
  const xpToNext = Number(grant?.xpToNext) || 0;
  const xpIntoLevelBefore =
    grant?.xpIntoLevelBefore != null
      ? Number(grant.xpIntoLevelBefore)
      : leveledUp
        ? 0
        : Math.max(0, xpIntoLevel - xpGained);
  const xpToNextBefore =
    grant?.xpToNextBefore != null ? Number(grant.xpToNextBefore) : xpToNext;

  const grantAnimKey = grant
    ? `${grant.totalXp}-${xpGained}-${levelBefore}-${levelAfter}-${xpIntoLevelBefore}-${xpIntoLevel}`
    : '';

  const { startPct, endPct } = useMemo(() => {
    const start = exploreUnknownXpBarPercent(xpIntoLevelBefore, xpToNextBefore);
    const end = exploreUnknownXpBarPercent(xpIntoLevel, xpToNext);
    return { startPct: start, endPct: end };
  }, [xpIntoLevelBefore, xpToNextBefore, xpIntoLevel, xpToNext]);

  useEffect(() => {
    if (!grant || !grantAnimKey) return undefined;

    setPhase('xp');
    setDisplayXp(0);
    setBarFillPct(startPct);

    const duration = 900;
    const barTarget = leveledUp ? 100 : endPct;
    const animStart = performance.now();
    let frame;

    const tick = (now) => {
      const p = Math.min(1, (now - animStart) / duration);
      setDisplayXp(Math.round(xpGained * p));
      setBarFillPct(startPct + (barTarget - startPct) * p);

      if (p < 1) {
        frame = requestAnimationFrame(tick);
        return;
      }
      if (leveledUp) {
        setBarFillPct(100);
        playSoundEffect('/sounds/levelup3.mp3');
        setPhase('levelup');
        return;
      }
      setBarFillPct(endPct);
      onCompleteRef.current();
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [grantAnimKey, grant, xpGained, leveledUp, startPct, endPct]);

  useEffect(() => {
    if (phase !== 'levelup') return undefined;
    const settle = window.setTimeout(() => setBarFillPct(endPct), 650);
    const tmr = window.setTimeout(() => onCompleteRef.current(), 2200);
    return () => {
      clearTimeout(settle);
      clearTimeout(tmr);
    };
  }, [phase, endPct]);

  if (!grant) return null;

  const bandBefore = xpIntoLevelBefore + xpToNextBefore;
  const bandAfter = xpIntoLevel + xpToNext;
  const displayLevel = phase === 'xp' ? levelBefore : levelAfter;
  const displayBand = phase === 'xp' ? bandBefore : bandAfter;
  const displayInto =
    phase === 'xp' ? Math.round((barFillPct / 100) * (bandBefore || 1)) : xpIntoLevel;

  const nextMilestoneBonus =
    grant.nextLevelMilestoneBonus ?? getImmediateNextMilestoneBonusFromLevel(levelBefore);
  const showNextMilestoneBonus =
    phase === 'xp' && nextMilestoneBonus != null && nextMilestoneBonus.icon;
  const nextBonusI18nKey =
    nextMilestoneBonus?.i18nKey || `levelBonus${nextMilestoneBonus?.level ?? ''}`;
  const nextBonusTitle = showNextMilestoneBonus
    ? t(`exploreUnknownPage.${nextBonusI18nKey}.title`, {
        defaultValue: nextMilestoneBonus.bonusName || nextMilestoneBonus.bonusId || '',
      })
    : '';

  return (
    <div
      className={`eu-xp-overlay eu-xp-overlay--after-battle${phase === 'levelup' ? ' eu-xp-overlay--levelup' : ''}`}
      role="status"
      aria-live="polite"
    >
      <div className="eu-xp-overlay-plaque">
        <div className="eu-xp-overlay-badge-wrap">
          <div className={`eu-xp-overlay-badge${phase === 'levelup' ? ' eu-xp-overlay-badge--flip' : ''}`}>
            <span className="eu-xp-overlay-badge-label">LV</span>
            <span className="eu-xp-overlay-badge-value">{displayLevel}</span>
          </div>
          {phase === 'levelup' ? <span className="eu-xp-overlay-shockwave" aria-hidden="true" /> : null}
        </div>
        <div className="eu-xp-overlay-info">
          <div className={`eu-xp-overlay-stage eu-xp-overlay-stage--xp${phase === 'levelup' ? ' eu-xp-overlay-stage--hidden' : ''}`}>
            <span className="eu-xp-overlay-eyebrow">
              {t('afterBattle.exploreUnknownXp.experienceGained')}
            </span>
            <div className="eu-xp-overlay-gain">
              <span className="eu-xp-overlay-gain-plus">+</span>
              <span className="eu-xp-overlay-gain-number">{displayXp.toLocaleString()}</span>
              <span className="eu-xp-overlay-gain-unit">XP</span>
            </div>
          </div>
          <div className={`eu-xp-overlay-stage eu-xp-overlay-stage--levelup${phase === 'levelup' ? '' : ' eu-xp-overlay-stage--hidden'}`}>
            <span className="eu-xp-overlay-eyebrow eu-xp-overlay-eyebrow--levelup">
              {t('afterBattle.exploreUnknownXp.levelUp')}
            </span>
            <div className="eu-xp-overlay-levelup-transition">
              <span className="eu-xp-overlay-levelup-from">{levelBefore}</span>
              <span className="eu-xp-overlay-levelup-arrow" aria-hidden="true">→</span>
              <span className="eu-xp-overlay-levelup-to">{levelAfter}</span>
            </div>
          </div>

          <div
            className="eu-xp-overlay-bar"
            role="progressbar"
            aria-valuenow={displayInto}
            aria-valuemin={0}
            aria-valuemax={displayBand || 1}
          >
            <div className="eu-xp-overlay-bar-fill" style={{ width: `${barFillPct}%` }} />
            <span className="eu-xp-overlay-bar-shine" aria-hidden="true" />
          </div>

          {showNextMilestoneBonus ? (
            <div className="eu-xp-overlay-next-bonus">
              <span className="eu-xp-overlay-next-bonus-label">
                {t('afterBattle.exploreUnknownXp.nextLevelBonus')}
              </span>
              <div className="eu-xp-overlay-next-bonus-preview">
                <div className="eu-xp-overlay-next-bonus-thumb-wrap">
                  <img
                    src={nextMilestoneBonus.icon}
                    alt=""
                    className="eu-xp-overlay-next-bonus-thumb"
                    draggable={false}
                  />
                  <span className="eu-xp-overlay-next-bonus-lv">{nextMilestoneBonus.level}</span>
                </div>
                <span className="eu-xp-overlay-next-bonus-name">{nextBonusTitle}</span>
              </div>
            </div>
          ) : null}

          <div className="eu-xp-overlay-footer">
            {phase === 'xp' && xpToNextBefore > 0 ? (
              <span className="eu-xp-overlay-foot-text">
                {t('exploreUnknownPage.levelTree.xpToNext', {
                  current: displayInto.toLocaleString(),
                  needed: (bandBefore || 0).toLocaleString(),
                })}
              </span>
            ) : null}
            {phase === 'levelup' && bandAfter > 0 ? (
              <span className="eu-xp-overlay-foot-text">
                {t('exploreUnknownPage.levelTree.xpToNext', {
                  current: xpIntoLevel.toLocaleString(),
                  needed: bandAfter.toLocaleString(),
                })}
              </span>
            ) : null}
            {(phase === 'xp' && xpToNextBefore <= 0) ||
            (phase === 'levelup' && bandAfter <= 0) ? (
              <span className="eu-xp-overlay-foot-text eu-xp-overlay-foot-text--max">
                {t('exploreUnknownPage.levelTree.maxLevel')}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
